import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardApi, defectsApi, workOrdersApi, maintenanceApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import OnboardingProgress from '../components/OnboardingProgress';
import DashboardComplianceCard from '../components/dashboard/DashboardComplianceCard';
import DashboardPlanUsageCard from '../components/dashboard/DashboardPlanUsageCard';
import { 
  Package, 
  AlertTriangle, 
  Wrench, 
  ClipboardCheck, 
  Calendar,
  ArrowRight,
  CheckCircle,
  XCircle,
  Clock,
  ChevronRight,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

// Tone palettes drive the number + icon-box colours. Applied via inline styles
// so they always win over inherited card text colour (Tailwind JIT was dropping
// the dynamically-passed arbitrary colour classes).
const METRIC_TONES = {
  neutral: { value: '#1A1A1A', iconBg: '#F4F2ED', iconBorder: '#E6E2D9', icon: '#59554D' },
  amber:   { value: '#C2410C', iconBg: '#FEF7E6', iconBorder: '#F5E3B3', icon: '#D97706' },
  red:     { value: '#DC2626', iconBg: '#FEF2F2', iconBorder: '#FBD3D3', icon: '#DC2626' },
};
const MUTED = { value: '#B5AFA3', iconBg: '#F4F2ED', iconBorder: '#E6E2D9', icon: '#B5AFA3' };

const MetricCard = ({ title, value, icon: Icon, tone = 'neutral', onClick, affordance }) => {
  const active = Number(value) > 0;
  const t = tone === 'neutral' ? METRIC_TONES.neutral : active ? METRIC_TONES[tone] : MUTED;
  return (
    <Card
      className="cursor-pointer transition-colors hover:border-[#1A1A1A]"
      onClick={onClick}
      style={{ borderRadius: 12 }}
      data-testid={`metric-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent style={{ padding: '16px 18px' }}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[10px] font-semibold tracking-[0.12em] uppercase text-[#8A857A]">{title}</p>
            <p className="mt-1.5 leading-none lc-tint" style={{ fontFamily: 'Manrope, sans-serif', fontWeight: 800, fontSize: 32, '--mv': t.value }}>{value}</p>
          </div>
          <div
            className="w-[38px] h-[38px] rounded-[10px] flex items-center justify-center shrink-0 border"
            style={{ backgroundColor: t.iconBg, borderColor: t.iconBorder }}
          >
            <Icon className="w-[18px] h-[18px]" style={{ color: t.icon }} />
          </div>
        </div>
        {affordance && (
          <p className="mt-3 pt-2.5 border-t border-[#F0EDE6] text-[11px] font-semibold text-[#59554D] flex items-center gap-1">
            {affordance} <ChevronRight className="w-3 h-3" />
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const QuickActionCard = ({ title, description, icon: Icon, onClick, testId }) => (
  <Card 
    className="card-hover cursor-pointer group"
    onClick={onClick}
    data-testid={testId}
  >
    <CardContent className="p-6">
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
          <Icon className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h3>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
        <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
      </div>
    </CardContent>
  </Card>
);

const RecentItem = ({ item, type }) => {
  const { formatDate } = useSettings();
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-[#FEF2F2] text-[#B91C1C] border-[#FECACA]';
      case 'medium': return 'bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]';
      default: return 'bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'resolved': return 'bg-[#ECFDF5] text-[#047857] border-[#A7F3D0]';
      case 'in_progress': return 'bg-[#FFFBEB] text-[#B45309] border-[#FDE68A]';
      default: return 'bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]';
    }
  };

  return (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{item.title}</p>
        <p className="text-xs text-muted-foreground">{formatDate(item.created_at)}</p>
        {type !== 'defect' && item.created_at && (() => {
          const days = Math.floor((Date.now() - new Date(item.created_at).getTime()) / 86400000);
          if (days < 0) return null;
          return <p className="text-[11px] text-[#8A857A] mt-0.5">{days} day{days === 1 ? '' : 's'} old</p>;
        })()}
      </div>
      <Badge className={`ml-2 ${type === 'defect' ? getSeverityColor(item.severity) : getStatusColor(item.status)}`}>
        {type === 'defect' ? item.severity : item.status}
      </Badge>
    </div>
  );
};

export const DashboardPage = () => {
  const [metrics, setMetrics] = useState(null);
  const [recentDefects, setRecentDefects] = useState([]);
  const [recentWorkOrders, setRecentWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [metricsRes, defectsRes, workOrdersRes] = await Promise.all([
        dashboardApi.getMetrics(),
        defectsApi.getAll({ status: 'open' }),
        workOrdersApi.getAll({ status: 'open' }),
      ]);
      
      setMetrics(metricsRes.data);
      setRecentDefects(defectsRes.data.slice(0, 5));
      setRecentWorkOrders(workOrdersRes.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl md:text-4xl font-bold uppercase tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Welcome, {user?.name}
        </h1>
        <p className="text-muted-foreground mt-1">Here's your fleet status overview</p>
      </div>

      {/* Onboarding progress ribbon — auto-hides when complete or dismissed */}
      <OnboardingProgress />

      {/* Free-plan asset usage — only renders for Free tenants past 70% of cap */}
      <DashboardPlanUsageCard />

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Assets"
          value={metrics?.total_assets || 0}
          icon={Package}
          affordance="View registry"
          onClick={() => navigate('/assets')}
        />
        <MetricCard
          title="Open Defects"
          value={metrics?.open_defects || 0}
          icon={AlertTriangle}
          tone="amber"
          affordance="View defects"
          onClick={() => navigate('/defects')}
        />
        <MetricCard
          title="Work Orders Due"
          value={metrics?.work_orders_due || 0}
          icon={Wrench}
          tone="red"
          affordance="View work orders"
          onClick={() => navigate('/work-orders')}
        />
        <MetricCard
          title="Inspections Today"
          value={metrics?.inspections_today || 0}
          icon={ClipboardCheck}
          affordance="View inspections"
          onClick={() => navigate('/inspections')}
        />
      </div>

      {/* Status Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-[#ECFDF5] border-[#A7F3D0]" style={{ borderRadius: 10 }}>
          <CardContent className="flex items-center gap-3" style={{ padding: '13px 15px' }}>
            <CheckCircle className="w-[22px] h-[22px] text-[#047857]" />
            <div>
              <p className="lc-tint font-mono font-bold text-[20px] leading-none" style={{ '--mv': '#047857' }}>{metrics?.assets_active || 0}</p>
              <p className="lc-tint text-[10px] font-semibold tracking-[0.11em] uppercase mt-[3px]" style={{ '--mv': '#0F766E' }}>Active</p>
            </div>
          </CardContent>
        </Card>
        <Card
          className={metrics?.assets_in_maintenance > 0 ? 'bg-[#FFFBEB] border-[#FDE68A]' : 'bg-[#FBFAF7] border-[#E2DED4]'}
          style={{ borderRadius: 10 }}
        >
          <CardContent className="flex items-center gap-3" style={{ padding: '13px 15px' }}>
            <Wrench className={`w-[22px] h-[22px] ${metrics?.assets_in_maintenance > 0 ? 'text-[#B45309]' : 'text-[#B5AFA3]'}`} />
            <div>
              <p className="lc-tint font-mono font-bold text-[20px] leading-none" style={{ '--mv': metrics?.assets_in_maintenance > 0 ? '#B45309' : '#8A857A' }}>{metrics?.assets_in_maintenance || 0}</p>
              <p className="lc-tint text-[10px] font-semibold tracking-[0.11em] uppercase mt-[3px]" style={{ '--mv': '#8A857A' }}>In Maintenance</p>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#FEF2F2] border-[#FECACA]" style={{ borderRadius: 10 }}>
          <CardContent className="flex items-center gap-3" style={{ padding: '13px 15px' }}>
            <XCircle className="w-[22px] h-[22px] text-[#B91C1C]" />
            <div>
              <p className="lc-tint font-mono font-bold text-[20px] leading-none" style={{ '--mv': '#B91C1C' }}>{metrics?.assets_out_of_service || 0}</p>
              <p className="lc-tint text-[10px] font-semibold tracking-[0.11em] uppercase mt-[3px]" style={{ '--mv': '#B91C1C' }}>Out of Service</p>
            </div>
          </CardContent>
        </Card>
        <Card
          className="bg-[#FFFBEB] border-[#FDE68A] cursor-pointer transition-colors hover:border-[#B45309]"
          style={{ borderRadius: 10 }}
          onClick={() => navigate('/maintenance')}
          data-testid="status-services-due"
        >
          <CardContent className="flex items-center gap-3" style={{ padding: '13px 15px' }}>
            <Calendar className="w-[22px] h-[22px] text-[#B45309]" />
            <div className="flex-1">
              <p className="lc-tint font-mono font-bold text-[20px] leading-none" style={{ '--mv': '#B45309' }}>{metrics?.services_due || 0}</p>
              <p className="lc-tint text-[10px] font-semibold tracking-[0.11em] uppercase mt-[3px]" style={{ '--mv': '#B45309' }}>Services Due</p>
            </div>
            <ChevronRight className="w-4 h-4 text-[#B45309]" />
          </CardContent>
        </Card>
      </div>

      {/* Fleet Compliance — drives renewal narrative + non-compliant drill-down */}
      <DashboardComplianceCard />

      {/* Quick Actions */}
      <div>
        <h2 className="text-xl font-bold uppercase tracking-tight mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Quick Actions
        </h2>
        <div className="grid md:grid-cols-3 gap-4">
          <QuickActionCard
            title="Scan Asset"
            description="Scan QR code to inspect"
            icon={ClipboardCheck}
            onClick={() => navigate('/scanner')}
            testId="quick-action-scan"
          />
          <QuickActionCard
            title="Report Defect"
            description="Log a new defect"
            icon={AlertTriangle}
            onClick={() => navigate('/defects/new')}
            testId="quick-action-defect"
          />
          <QuickActionCard
            title="Create Work Order"
            description="Schedule maintenance"
            icon={Wrench}
            onClick={() => navigate('/work-orders/new')}
            testId="quick-action-workorder"
          />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg uppercase tracking-wide flex items-center justify-between">
              <span>Recent Defects</span>
              <Button variant="ghost" size="sm" onClick={() => navigate('/defects')} data-testid="view-all-defects">
                View All
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentDefects.length > 0 ? (
              recentDefects.map(defect => (
                <RecentItem key={defect.id} item={defect} type="defect" />
              ))
            ) : (
              <p className="text-muted-foreground text-center py-8">No open defects</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg uppercase tracking-wide flex items-center justify-between">
              <span>Pending Work Orders</span>
              <Button variant="ghost" size="sm" onClick={() => navigate('/work-orders')} data-testid="view-all-workorders">
                View All
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentWorkOrders.length > 0 ? (
              recentWorkOrders.map(wo => (
                <RecentItem key={wo.id} item={wo} type="workorder" />
              ))
            ) : (
              <p className="text-muted-foreground text-center py-8">No pending work orders</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
